import { type FC, memo, type MouseEvent, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { css, cx } from "@stubhub/broadway-foundation/css";
import {
  Button,
  ButtonKind,
  ButtonLink,
  ButtonSize,
  ButtonVariant,
  LegacyTagSize,
  LegacyTagVariant,
  Tag,
  Text,
} from "@stubhub/broadway-web";
import type { FlagCountry } from "@stubhub/broadway-web/icons/flags";
import { CarIcon, CheckIcon, NotificationIcon } from "@stubhub/broadway-web/icons/functional";
import {
  EventGridListItem,
  EventGridListItemEventState,
  EventGridListItemEventType,
  EventGridListItemKind,
  EventGridListItemTags,
  EventGridListItemVariant,
  useFormattedEventDateTime,
} from "@stubhub/ensemble-discovery";
import type { EventListGridItem } from "src/http/types";
import { Content, FormatContent, useContent } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import type { PartnerTheme } from "src/types/shared/partners";
import {
  eventContent,
  eventDetailsRecipe,
  eventInfo,
  eventTextInfo,
  homeGameIndicator,
  teamLogo,
  teamNameRecipe,
  textDisabled,
} from "src/website-apps/responsive/stubhub-performer/components/calendar/mobile/styles";
import { NotificationOptionEnum, useStoredNotifications } from "src/website-apps/shared/components/notifications";
import { useIsTicketsNotAvailable } from "src/website-apps/shared/hooks/useIsTicketsNotAvailable";
import { useMerchMessageTags } from "src/website-apps/shared/hooks/useMerchMessageTags";

// Reset Ensemble's container layout so the mobile card's row-flex + alignment wins.
const eventGridListItemMobileCard = css({
  paddingBlock: "primitives.0",
  paddingInline: "primitives.0",
  textAlign: "start",
  _hover: { backgroundColor: "bg.surface" },
  _active: { backgroundColor: "bg.surface" },
  position: "relative",
  display: "flex",
  alignItems: "center",
  columnGap: "primitives.100",
  rowGap: "primitives.100",
  borderBlockWidth: "primitives.25",
  borderInlineWidth: "primitives.25",
  borderBlockStyle: "solid",
  borderInlineStyle: "solid",
  borderBlockColor: "border",
  borderInlineColor: "border",
  borderStartRadius: "container",
  borderEndRadius: "container",
  justifyContent: "space-between",
});

interface Props {
  event: EventListGridItem;
  isHomeGame: boolean;
  teamThemes: {
    homeTeamData: { theme: Maybe<PartnerTheme> } | null;
    awayTeamData: { theme: Maybe<PartnerTheme> } | null;
  };
  primaryColor?: string | undefined;
  openNotificationHandler: (eventId: number) => void;
}

export const SingleEventBodyMobile: FC<Props> = memo(
  ({ event, isHomeGame, teamThemes, primaryColor, openNotificationHandler }) => {
    const storePageEvent = useStorePageEvent();
    const { formattedTime } = useFormattedEventDateTime(
      event.eventMetadata?.common?.eventStartDateTime,
      event.eventMetadata?.common?.eventEndDateTime ?? undefined,
      event.eventMetadata?.common?.venueTimeZoneOffset,
      event.onSaleDateTimeData?.onSaleDateTime
        ? dayjs(event.onSaleDateTimeData.onSaleDateTime).unix() * 1000
        : undefined,
      event.eventMetadata?.common?.passLength ?? undefined,
    );
    const { noTicketsAvailable } = useIsTicketsNotAvailable(event);
    const { isNotificationStored } = useStoredNotifications();
    const isPlayoffGame = !!event.eventMetadata?.sports?.playoffSeriesMetadata;
    const isParkingEvent = !!event.isParkingEvent;
    const { tags: merchTags, inViewTrackers: merchInViewTrackers } = useMerchMessageTags({
      event,
      disabled: noTicketsAvailable,
      maxTags: 1,
    });

    const isSingleEventNotificationSignedUp = isNotificationStored(event.eventId, NotificationOptionEnum.EVENT);

    /** "Sold out" */
    const soldOutText = useContent("cid$#3979713#");
    /** "Home" */
    const homeText = useContent("cid$#3979720#");
    /** "Away" */
    const awayText = useContent("cid$#3979719#");
    /** "TBA" */
    const timeTbaText = useContent("cid$#3860643#");

    const secondaryTeamMetadata = event.eventMetadata?.sports
      ? event.eventMetadata.metadataForAllCategoriesInEvent?.[
          isHomeGame ? (event.eventMetadata.sports.awayTeamId ?? -1) : (event.eventMetadata.sports.homeTeamId ?? -1)
        ]
      : null;

    const secondaryTeamTheme = isHomeGame ? teamThemes.awayTeamData?.theme : teamThemes.homeTeamData?.theme;

    const handleNotificationClick = useCallback(() => {
      openNotificationHandler(event.eventId);
    }, [event.eventId, openNotificationHandler]);

    const handleSeeTicketsClick = useCallback(() => {
      storePageEvent(PE_TYPE.CLICK, PE_KEYS.EVENTS_CALENDAR_EVENT_CARD, String(event.eventId));
    }, [event.eventId, storePageEvent]);

    // Outer click on EventGridListItem.
    // - Active state: outer is <a href={event.url}>; browser navigates. Fire analytics.
    // - Sold-out state: outer is <button>; open notification modal (unless already signed up).
    const handleCardClick = useCallback(() => {
      if (noTicketsAvailable) {
        if (isSingleEventNotificationSignedUp) return;
        handleNotificationClick();
        return;
      }
      handleSeeTicketsClick();
    }, [noTicketsAvailable, isSingleEventNotificationSignedUp, handleNotificationClick, handleSeeTicketsClick]);

    // Inner CTA buttons need stopPropagation to prevent the outer onClick from
    // double-firing (analytics on active events; modal on sold-out events).
    const handleNotificationButtonClick = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        handleNotificationClick();
      },
      [handleNotificationClick],
    );

    const handleSeeTicketsButtonClick = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        handleSeeTicketsClick();
      },
      [handleSeeTicketsClick],
    );

    // From-price is gated by TPerformerFromPriceInEventListV8 / MPerformerFromPriceInEventListV8
    // upstream — only populated when the user is in variant.
    const fromPrice = event.contextSensitiveFormattedMinPrice ?? null;

    const eventCta = useMemo(() => {
      if (noTicketsAvailable) {
        return isSingleEventNotificationSignedUp ? (
          <Button
            variant={ButtonVariant.Outline}
            kind={ButtonKind.Neutral}
            startIcon={CheckIcon}
            size={ButtonSize.Small}
            disabled
          >
            {/* "On the list" */}
            <Content id="cid$#3994932#" />
          </Button>
        ) : (
          <Button
            onClick={handleNotificationButtonClick}
            variant={ButtonVariant.Outline}
            kind={ButtonKind.Neutral}
            startIcon={NotificationIcon}
            size={ButtonSize.Small}
          >
            <Content id="cid$#3375073#" />
          </Button>
        );
      }
      return (
        <ButtonLink
          key={event.eventId}
          href={event.url ?? undefined}
          onClick={handleSeeTicketsButtonClick}
          variant={ButtonVariant.Outline}
          kind={ButtonKind.Neutral}
          size={ButtonSize.Small}
          target="_blank"
        >
          <Text variant="label" size="small">
            {fromPrice ? (
              /* "From {0}" */
              <FormatContent id="cid$#4099723#" params={[fromPrice]} />
            ) : isParkingEvent ? (
              <Content id="cid$#3528590#" />
            ) : (
              <Content id="cid$#3177387#" />
            )}
          </Text>
        </ButtonLink>
      );
    }, [
      event.eventId,
      event.url,
      handleNotificationButtonClick,
      handleSeeTicketsButtonClick,
      isParkingEvent,
      noTicketsAvailable,
      isSingleEventNotificationSignedUp,
      fromPrice,
    ]);

    return (
      <EventGridListItem
        href={event.url ?? "#"}
        target="_blank"
        onClick={handleCardClick}
        kind={EventGridListItemKind.Neutral}
        variant={EventGridListItemVariant.Ghost}
        eventState={noTicketsAvailable ? EventGridListItemEventState.SoldOut : EventGridListItemEventState.Active}
        eventType={isParkingEvent ? EventGridListItemEventType.Parking : EventGridListItemEventType.Event}
        eventTitle={event.name}
        venueName={event.venueName ?? ""}
        venueLocation={[event.venueCity, event.venueStateProvince].filter(Boolean).join(", ")}
        venueCountryCode={(event.countryCode ?? "") as FlagCountry}
        startTimeMillis={event.eventMetadata?.common?.eventStartDateTime}
        endTimeMillis={event.eventMetadata?.common?.eventEndDateTime ?? undefined}
        venueTimeZoneOffsetMillis={event.eventMetadata?.common?.venueTimeZoneOffset}
        passLength={event.eventMetadata?.common?.passLength ?? undefined}
        isDateConfirmed={event.isDateConfirmed}
        isTimeConfirmed={event.isTimeConfirmed}
        classes={{ container: eventGridListItemMobileCard }}
      >
        {isHomeGame && primaryColor && <div className={homeGameIndicator} style={{ backgroundColor: primaryColor }} />}
        {secondaryTeamTheme ? (
          <div className={eventContent}>
            <img
              className={teamLogo}
              src={secondaryTeamTheme.transparentPartnerLogoUrl ?? undefined}
              alt={`${secondaryTeamMetadata?.shortName ?? "team"} logo`}
              role="presentation"
            />
            <div className={eventTextInfo}>
              {isParkingEvent ? (
                <Tag startIcon={CarIcon} size={LegacyTagSize.Small}>
                  {/* "Parking" */}
                  <Content id="cid$#3987095#" />
                </Tag>
              ) : (
                isPlayoffGame &&
                event.eventMetadata?.sports?.playoffSeriesMetadata?.roundOfPlayoff && (
                  <Tag variant={LegacyTagVariant.Regular} size={LegacyTagSize.Small} style={{ marginBottom: "4px" }}>
                    {event.eventMetadata.sports.playoffSeriesMetadata.roundOfPlayoff}
                  </Tag>
                )
              )}
              <Text
                variant="label"
                size="small"
                className={teamNameRecipe({ availability: noTicketsAvailable ? "unavailable" : "available" })}
              >
                {isHomeGame ? /* "vs" */ <Content id="cid$#3561795#" /> : /* "@" */ <Content id="cid$#3561783#" />}{" "}
                {secondaryTeamMetadata?.shortName ?? /* "TBD" */ <Content id="cid$#3983126#" />}
              </Text>
              <Text
                variant="label"
                size="extra-small"
                className={eventDetailsRecipe({ availability: noTicketsAvailable ? "unavailable" : "available" })}
              >
                {noTicketsAvailable
                  ? soldOutText
                  : `${event.isTimeConfirmed === false ? timeTbaText : formattedTime} • ${isHomeGame ? homeText : awayText}`}
              </Text>
            </div>
          </div>
        ) : (
          <div className={eventInfo}>
            <Text variant="label" size="small" className={cx(noTicketsAvailable && textDisabled)}>
              {event.name}
            </Text>
            <Text
              variant="label"
              size="extra-small"
              className={eventDetailsRecipe({ availability: noTicketsAvailable ? "unavailable" : "available" })}
            >
              {noTicketsAvailable
                ? soldOutText
                : `${event.isTimeConfirmed === false ? timeTbaText : formattedTime} • ${isHomeGame ? homeText : awayText}`}
            </Text>
          </div>
        )}
        {merchTags.length > 0 && <EventGridListItemTags legacy tags={merchTags} />}
        {eventCta}
        {merchInViewTrackers}
      </EventGridListItem>
    );
  },
);
SingleEventBodyMobile.displayName = "SingleEventBodyMobile";
